import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Loader2, Plus, Trash2, Save, KeyRound, X, Users, Power,
} from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

/**
 * Admin · Partner Agencies manager.
 *
 * Studio manually creates accounts (agency name + email + password) for
 * partner booking agencies. All partners see the same read-only view of
 * incoming applications. Toggle `active` to revoke access without deleting.
 */
export default function PartnersManager() {
  const nav = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null); // partner id
  const [form, setForm] = useState({ agency_name: "", email: "", password: "" });
  const [newPw, setNewPw] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/admin/partners")
      .then((r) => setPartners(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login");
      return;
    }
    load();
  }, [nav]);

  const createPartner = async (e) => {
    e.preventDefault();
    if (!form.agency_name.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error("Fill agency name, email, and a password ≥ 8 characters");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/partners", form);
      toast.success(`${form.agency_name} added`);
      setForm({ agency_name: "", email: "", password: "" });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create partner");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/partners/${p.id}`, { active: !p.active });
      toast.success(p.active ? `${p.agency_name} disabled` : `${p.agency_name} enabled`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const resetPassword = async (p) => {
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await api.put(`/admin/partners/${p.id}`, { password: newPw });
      toast.success(`Password reset for ${p.agency_name}`);
      setResetting(null);
      setNewPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.agency_name}" partner account? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/partners/${p.id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div data-testid="admin-partners-page" className="min-h-screen bg-white text-[#0A0A0A] pt-8 pb-24">
      <div className="max-w-5xl mx-auto px-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black">
          <ChevronLeft size={14} /> Back to dashboard
        </Link>

        <div className="mt-6 flex items-end justify-between">
          <div>
            <p className="label-overline text-zinc-500 flex items-center gap-2">
              <Users size={12} /> Partner Agencies
            </p>
            <h1 className="font-serif text-5xl mt-3">Booking-agency access.</h1>
            <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
              Issue individual accounts for international partner agencies. All partners see
              the same read-only feed of incoming applications. Contact info stays hidden per
              application until you release it from the Applications tab.
            </p>
          </div>
        </div>

        {/* Create form */}
        <form
          onSubmit={createPartner}
          className="mt-10 border border-black/10 p-6 grid grid-cols-1 md:grid-cols-4 gap-4"
        >
          <div>
            <Label className="label-overline text-zinc-500">Agency name</Label>
            <Input
              data-testid="partner-agency-name-input"
              placeholder="e.g. IMG Paris"
              value={form.agency_name}
              onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
              className="mt-2 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
            />
          </div>
          <div>
            <Label className="label-overline text-zinc-500">Email</Label>
            <Input
              data-testid="partner-email-input"
              type="email"
              placeholder="paris@img.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
            />
          </div>
          <div>
            <Label className="label-overline text-zinc-500">Password</Label>
            <Input
              data-testid="partner-password-input"
              type="password"
              placeholder="≥ 8 characters"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              data-testid="create-partner-btn"
              disabled={creating}
              className="btn-primary w-full justify-center flex items-center gap-2"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add partner
            </button>
          </div>
        </form>

        {/* Table */}
        <div className="mt-10">
          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="animate-spin text-zinc-500" />
            </div>
          ) : partners.length === 0 ? (
            <p data-testid="no-partners" className="py-16 text-center text-zinc-500 border border-dashed border-black/10">
              No partner agencies yet.
            </p>
          ) : (
            <div className="border border-black/10 divide-y divide-black/10">
              {partners.map((p) => (
                <div
                  key={p.id}
                  data-testid={`partner-row-${p.id}`}
                  className="p-5 grid grid-cols-1 md:grid-cols-[1.4fr_1.4fr_0.7fr_auto] gap-4 items-center"
                >
                  <div>
                    <p className="font-medium">{p.agency_name}</p>
                    <p className="text-xs text-zinc-500 mt-1">Created {new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <p className="text-sm text-zinc-700 break-all">{p.email}</p>
                  <div>
                    <span
                      className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 ${
                        p.active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-600"
                      }`}
                    >
                      {p.active ? "Active" : "Disabled"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-end flex-wrap">
                    <button
                      data-testid={`toggle-active-${p.id}`}
                      onClick={() => toggleActive(p)}
                      className="text-xs px-3 py-2 border border-black/20 hover:bg-black hover:text-white transition-colors flex items-center gap-1"
                    >
                      <Power size={12} /> {p.active ? "Disable" : "Enable"}
                    </button>
                    <button
                      data-testid={`reset-password-${p.id}`}
                      onClick={() => setResetting(resetting === p.id ? null : p.id)}
                      className="text-xs px-3 py-2 border border-black/20 hover:bg-black hover:text-white transition-colors flex items-center gap-1"
                    >
                      <KeyRound size={12} /> Reset
                    </button>
                    <button
                      data-testid={`delete-partner-${p.id}`}
                      onClick={() => remove(p)}
                      className="text-xs px-3 py-2 border border-red-400 text-red-600 hover:bg-red-500 hover:text-white transition-colors flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>

                  {resetting === p.id && (
                    <div className="md:col-span-4 bg-zinc-50 border border-black/10 p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                      <div className="flex-1 w-full">
                        <Label className="label-overline text-zinc-500">New password</Label>
                        <Input
                          data-testid={`new-password-${p.id}`}
                          type="password"
                          value={newPw}
                          onChange={(e) => setNewPw(e.target.value)}
                          placeholder="≥ 8 characters"
                          className="mt-2 bg-white border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          data-testid={`save-password-${p.id}`}
                          onClick={() => resetPassword(p)}
                          className="btn-primary flex items-center gap-2"
                        >
                          <Save size={12} /> Save
                        </button>
                        <button
                          onClick={() => { setResetting(null); setNewPw(""); }}
                          className="px-3 py-2 border border-black/20 text-sm flex items-center gap-1 hover:bg-black/5"
                        >
                          <X size={12} /> Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
